import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { AlertDetails } from '../../../API/types/Alerts';
import Tags from '../../../components/common/Tags';
import { IconKeys } from '../../../components/common/Icon/IconKeys';
import { alertEventNames, alertEventsMap } from '../../Alerts/constants/alertEventsMap';
import { alertSensorMap } from '../../Alerts/constants/alertSensorMap';
import { capitalizeRiskLevel, generateColorBasedOnRisk } from '../helpers';
import {
  getFormattedDateWithComma,
  getTime,
  getFormattedDateAndTime,
  getDateWeek,
  getTimeZone,
} from '../../../utils/dateUtils';
// import { generateVehicleTypeIcon } from '../../VehicleDetails/helpers';
import OvertimeTag from './OvertimeTag';
import { convertMsToKm } from '../../../utils/unitsUtils';
import HiddenDetails, { HiddenDetailTagItem } from '../../../components/common/Tags/HiddenDetails/HiddenDetails';
import { TagsConfig, resolveTagsForType } from '../../../components/common/Tags/HiddenDetails/detailsHiddenTags';
import { ThemeType } from '../../../theme/themeType';
import useGetTripMerge from '../../../API/hooks/trips/useGetTripMerge';
import { buildCurrentPath, getAlertTripNavigationTarget } from '../../../utils/alertNavigation';

const accelerationEvents = [
  alertEventNames.DevStreamEventHighG,
  alertEventNames.DevStreamEventHardBrake,
  alertEventNames.DevStreamEventHardTurn,
  alertEventNames.DevStreamEventHardAcceleration,
];

const blockageStatus = [
  'No Obstruction',
  'Partial Obstruction',
  'Total Obstruction',
];

type ShownAlertTagKey =
  | 'time'
  | 'date'
  // | 'vehicle'
  | 'alertType'
  | 'trip'
  | 'duration'
  | 'configuredSpeedLimit'
  | 'maxG';

// This config supports per-alert overrides and can be reused by other detail pages.
// To show tags not in "More details.." for all alerts, add them to defaultTags
// To show tags not in "More details.." only for one alert, add them to tagsByType
const ALERT_DETAILS_SHOWN_TAGS_CONFIG: TagsConfig<ShownAlertTagKey> = {
  defaultTags: [
    'time',
    'date',
    // 'vehicle',
    'alertType',
    'trip',
  ],
  tagsByType: {
    [alertEventNames.DevStreamEventOverspeed]: [
      'duration',
      'configuredSpeedLimit',
    ],
    [alertEventNames.DevStreamEventDrowsiness]: [
      'duration',
    ],
    [alertEventNames.DevStreamEventEyesOffRoad]: [
      'duration',
    ],
    [alertEventNames.DevStreamEventHardBrake]: [
      'maxG',
    ],
    [alertEventNames.DevStreamEventHardAcceleration]: [
      'maxG',
    ],
    [alertEventNames.DevStreamEventHardTurn]: [
      'maxG',
    ],
    [alertEventNames.DevStreamEventHighG]: [
      'maxG',
    ],
  },
};

function AlertTags({ alert }: { alert: AlertDetails }): JSX.Element {
  const theme = useTheme() as ThemeType;
  const { badge } = alertEventsMap[alert?.name as string] || {};
  const navigate = useNavigate();
  const location = useLocation();
  const { data: tripMerge } = useGetTripMerge(alert.fleetId);
  const tripNavigationTarget = useMemo(
    () => getAlertTripNavigationTarget(alert, !!tripMerge?.clientTripMergeEnabled),
    [alert, tripMerge?.clientTripMergeEnabled],
  );

  const goToTrip = (): void => {
    if (!tripNavigationTarget) {
      return;
    }
    navigate(`/trips/${tripNavigationTarget.tripId}`, {
      state: {
        isMergedTrip: tripNavigationTarget.isMergedTrip,
        from: buildCurrentPath(location.pathname, location.search),
        alertDetailsState: location.state,
      },
    });
  };

  const shownTagSet = useMemo(
    () => {
      const tagsForType = resolveTagsForType(ALERT_DETAILS_SHOWN_TAGS_CONFIG, alert.name);

      // Type-specific tags are additive to defaults (defaults are always visible).
      return new Set([...ALERT_DETAILS_SHOWN_TAGS_CONFIG.defaultTags, ...tagsForType]);
    },
    [alert.name],
  );

  const isFcwEvent = alert.name === alertEventNames.DevStreamEventFcw;
  const isPcwEvent = alert.name === alertEventNames.DevStreamEventPcw;
  const isLdwEvent = alert.name === alertEventNames.DevStreamEventLdw;
  const isEyesOffRoadEvent = alert.name === alertEventNames.DevStreamEventEyesOffRoad;
  const isMobilePhoneUsageEvent = alert.name === alertEventNames.DevStreamEventMobilePhoneUsage;
  const isCameraBlockageEvent = alert.name === alertEventNames.DevStreamEventCameraBlockage
    || alert.name === alertEventNames.DevStreamEventCameraBlockageInterior
    || alert.name === alertEventNames.DevStreamEventCameraBlockageExterior;
  const isAccelerationEvent = accelerationEvents.includes(alert.name);
  const gravityConstant = 9.81;

  const getAxisGValue = (acceleration?: number): number | null => {
    if (acceleration === undefined) {
      return null;
    }

    return Math.abs(acceleration) / gravityConstant;
  };

  const formatG = (value: number | null): string => (
    value === null ? 'N/A' : value.toFixed(2)
  );

  const formatAcceleration = (value?: number): string => (
    value === undefined ? 'N/A' : `${value}`
  );

  const gX = getAxisGValue(alert.event?.maxXAcceleration);
  const gY = getAxisGValue(alert.event?.maxYAcceleration);
  const gZ = getAxisGValue(alert.event?.maxZAcceleration);

  const maxGValue = useMemo((): number | null => {
    if (alert.name === alertEventNames.DevStreamEventHardBrake
      || alert.name === alertEventNames.DevStreamEventHardAcceleration) {
      return gX;
    }

    if (alert.name === alertEventNames.DevStreamEventHardTurn) {
      return gY;
    }

    if (alert.name === alertEventNames.DevStreamEventHighG) {
      if (gX === null || gY === null || gZ === null) {
        return null;
      }

      return Math.sqrt((gX ** 2) + (gY ** 2) + (gZ ** 2));
    }

    return null;
  }, [alert.name, gX, gY, gZ]);

  const showOvertimeLabel = (): boolean => {
    switch (alert?.event?.level) {
      case 1:
        return !alert.tripId;
        break;
      case 2:
        return (new Date(alert.createdAt).getDay() === new Date(Date.now()).getDay());
        break;
      case 3:
        return getDateWeek(new Date(alert.createdAt)) === getDateWeek(new Date(Date.now()));
        break;
      case 4:
        return (new Date(alert.createdAt).getMonth() === new Date(Date.now()).getMonth());
        break;
      default:
        return false;
    }
  };

  let locationLabel = 'Location is not available';
  if (alert.locationName && alert.locationName !== 'N/A') {
    locationLabel = alert.locationName;
  } else if (alert.lat != null || alert.lon != null) {
    locationLabel = `lat:${alert.lat}, lon:${alert.lon}`;
  }

  const moreDetailsContent = (
    <>
      {!shownTagSet.has('time') && (
        <Tags.LabeledIcon
          label={`${getTime(alert.eventDate)} (${getTimeZone(alert.eventDate)})`}
          icon={IconKeys.TIME}
          flexible
        />
      )}
      {!shownTagSet.has('date') && (
        <Tags.LabeledIcon
          label={getFormattedDateWithComma(alert.eventDate)}
          icon={IconKeys.DATE}
        />
      )}
      {/* {!shownTagSet.has('vehicle') && (
        <Tags.LabeledIcon
          label={alert.vehicle?.licensePlateNumber}
          icon={generateVehicleTypeIcon(alert.vehicle?.category)}
          onClick={() => alert.event?.vehicleId && navigate(`/vehicles/${alert.event.vehicleId}`)}
        />
      )} */}
      {!shownTagSet.has('alertType') && (
        <Tags.Toggler
          type={alertSensorMap[alert.sensor]}
          trigger={badge}
        />
      )}
      <Tags.LabeledIcon
        flexible
        label={locationLabel}
        icon={IconKeys.LOCATION}
        iconClass="map-icon"
      />
      {alert.event?.eventSharedData?.alt !== undefined && (
        <Tags.Label
          label={`Altitude: ${alert.event.eventSharedData.alt}`}
        />
      )}
      {alert.event?.confidence !== undefined && (isLdwEvent) && (
        <Tags.Label
          label={`Confidence: ${alert.event.confidence}`}
        />
      )}
      {alert.event?.distance !== undefined && (
        <Tags.Label
          label={`Distance: ${alert.event.distance}`}
        />
      )}
      {alert.event?.distanceX !== undefined && (isFcwEvent || isPcwEvent) && (
        <Tags.Label
          label={`Distance X: ${alert.event.distanceX}`}
        />
      )}
      {alert.event?.distanceY !== undefined && (isFcwEvent || isPcwEvent) && (
        <Tags.Label
          label={`Distance Y: ${alert.event.distanceY}`}
        />
      )}
      {!shownTagSet.has('duration') && alert.event?.durationMs !== undefined && (
        <Tags.Label
          label={`Duration (ms): ${alert.event.durationMs}`}
        />
      )}
      {alert.event?.endTimestamp !== undefined && (
        <Tags.Label
          label={`End time: ${alert.event.endTimestamp
            ? getFormattedDateAndTime(new Date(alert.event.endTimestamp * 1000), true, true)
            : 'N/A'}`}
        />
      )}
      {alert.event?.faceData !== undefined && (
        <Tags.Label
          label={`Face data: ${alert.event.faceData}`}
        />
      )}
      {alert.event?.faceNotVisible !== undefined && (
        <Tags.Label
          label={`Face ${alert.event.faceNotVisible ? 'not' : 'is'} visible`}
        />
      )}
      {alert.event?.eyesNotVisible !== undefined && (
        <Tags.Label
          label={`Eyes ${alert.event.eyesNotVisible ? 'not' : 'are'} visible`}
        />
      )}
      {alert.event?.antiSpoofing !== undefined && (
        <Tags.Label
          label={`Anti spoofing: ${alert.event.antiSpoofing}`}
        />
      )}
      {alert.event?.useMask !== undefined && (
        <Tags.Label
          label={alert.event.useMask ? 'Mask on face' : 'No mask on face'}
        />
      )}
      {alert.event?.headNotInRange !== undefined && (
        <Tags.Label
          label={alert.event.headNotInRange ? 'Head position out of range' : 'Head position in range'}
        />
      )}
      {alert.event?.useGlasses !== undefined && (
        <Tags.Label
          label={alert.event.useGlasses
            ? 'Non-transparent glasses on face'
            : 'No non-transparent glasses on face'}
        />
      )}
      {alert.event?.pitchAngle !== undefined && (
        <Tags.Label
          label={`Pitch angle: ${alert.event.pitchAngle}`}
        />
      )}
      {alert.event?.rollAngle !== undefined && (
        <Tags.Label
          label={`Roll angle: ${alert.event.rollAngle}`}
        />
      )}
      {alert.event?.yawAngle !== undefined && (
        <Tags.Label
          label={`Yaw angle: ${alert.event.yawAngle}`}
        />
      )}
      {alert.event?.hEyesAngleDeg !== undefined && !isEyesOffRoadEvent && !isMobilePhoneUsageEvent && (
        <Tags.Label
          label={`Horizontal eyes angle: ${alert.event.hEyesAngleDeg}`}
        />
      )}
      {alert.event?.vEyesAngleDeg !== undefined && !isEyesOffRoadEvent && !isMobilePhoneUsageEvent && (
        <Tags.Label
          label={`Vertical eyes angle: ${alert.event.vEyesAngleDeg}`}
        />
      )}
      {alert.event?.status !== undefined && (
        <Tags.Label
          label={`Status: ${alert.event.status}`}
        />
      )}
      {alert.event?.proximityTimeSec !== undefined && (
        <Tags.Label
          label={`Proximity time: ${alert.event.proximityTimeSec} sec`}
        />
      )}
      {alert.event?.startTime !== undefined && (
        <Tags.Label
          label={`Start time: ${getFormattedDateAndTime(new Date(alert.event.startTime * 1000))}`}
        />
      )}
      {alert.event?.targetDistanceCm !== undefined && (
        <Tags.Label
          label={`Target distance: ${alert.event.targetDistanceCm} cm`}
        />
      )}
      {alert.event?.targetLatCoordCm !== undefined && (
        <Tags.Label
          label={`Target lat coord: ${alert.event.targetLatCoordCm} cm`}
        />
      )}
      {alert.event?.targetLonCoordCm !== undefined && (
        <Tags.Label
          label={`Target lon coord: ${alert.event.targetLonCoordCm} cm`}
        />
      )}
      {alert.event?.targetLatSpeedMs !== undefined && (
        <Tags.Label
          label={`Target lat speed: ${convertMsToKm(alert.event.targetLatSpeedMs)} km/h`}
        />
      )}
      {alert.event?.targetLonSpeedMs !== undefined && (
        <Tags.Label
          label={`Target lon speed: ${convertMsToKm(alert.event.targetLonSpeedMs)} km/h`}
        />
      )}
      {alert.event?.targetLongCoordCm !== undefined && (
        <Tags.Label
          label={`Target long coord: ${alert.event.targetLongCoordCm} cm`}
        />
      )}
      {alert.event?.ttcMs !== undefined && (
        <Tags.Label
          label={`TTC: ${convertMsToKm(alert.event.ttcMs)} km/h`}
        />
      )}
      {!shownTagSet.has('configuredSpeedLimit')
        && (alert.event?.configSpeedLimit || alert.event?.detectedSpeedLimitMs !== undefined) && (
        <Tags.Label
          label={`Configured Speed Limit: ${
            alert.event?.configSpeedLimit ? alert.event?.configSpeedLimit : 0
          } km/h`}
        />
      )}
      {!!alert.event?.maxSpeedMs && (
        <Tags.Label
          label={`Max speed: ${convertMsToKm(alert.event.maxSpeedMs)} km/h`}
        />
      )}
      {alert.event?.lateralDistanceCm !== undefined && (
        <Tags.Label
          label={`Lateral distance: ${alert.event.lateralDistanceCm} cm`}
        />
      )}
      {alert.event?.lateralSpeedMs !== undefined && (
        <Tags.Label
          label={`Lateral speed: ${convertMsToKm(alert.event.lateralSpeedMs)} km/h`}
        />
      )}
      {alert.event?.targetLaneLatCoord !== undefined && (
        <Tags.Label
          label={`Target lane lat coord: ${alert.event.targetLaneLatCoord}`}
        />
      )}
      {alert.event?.targetLaneLonCoord !== undefined && (
        <Tags.Label
          label={`Target lane lon coord: ${alert.event.targetLaneLonCoord}`}
        />
      )}
      {alert.event?.latLevel !== undefined && (
        <Tags.Label
          label={`Lat level: ${alert.event.latLevel}`}
        />
      )}
      {alert.event?.longLevel !== undefined && (
        <Tags.Label
          label={`Long level: ${alert.event.longLevel}`}
        />
      )}
      {alert.event?.maxLatAccelerationMs2 !== undefined && (
        <Tags.Label
          label={`Max lat acceleration: ${alert.event.maxLatAccelerationMs2} m/s2`}
        />
      )}
      {alert.event?.maxLonAccelerationMs2 !== undefined && (
        <Tags.Label
          label={`Max lon acceleration: ${alert.event.maxLonAccelerationMs2} m/s2`}
        />
      )}
      {alert.event?.maxLongAccelerationMs2 !== undefined && (
        <Tags.Label
          label={`Max long acceleration: ${alert.event.maxLongAccelerationMs2} m/s2`}
        />
      )}
      {alert.event?.maxVerAccelerationMs2 !== undefined && (
        <Tags.Label
          label={`Max ver acceleration: ${alert.event.maxVerAccelerationMs2} m/s2`}
        />
      )}
      {alert.event?.maxVertAccelerationMs2 !== undefined && (
        <Tags.Label
          label={`Max vert acceleration: ${alert.event.maxVertAccelerationMs2} m/s2`}
        />
      )}
      {alert.event?.maxAlertValue !== undefined && alert.sensor !== 'IMU' && (
        <Tags.Label
          label={`Max alert value: ${alert.event.maxAlertValue}`}
        />
      )}
      {alert.event?.vertLevel !== undefined && (
        <Tags.Label
          label={`Vert level: ${alert.event.vertLevel}`}
        />
      )}
      {alert.event?.relativeVelocityMs !== undefined && (
        <Tags.Label
          label={`Relative velocity: ${convertMsToKm(alert.event.relativeVelocityMs)} km/h`}
        />
      )}
      {alert.event?.targetVehicleSpeedMs !== undefined && (
        <Tags.Label
          label={`Target vehicle speed: ${convertMsToKm(alert.event.targetVehicleSpeedMs)} km/h`}
        />
      )}
      {isCameraBlockageEvent && alert.event?.blockageTotal !== undefined && (
        <Tags.Label
          label={`Blockage total: ${blockageStatus[alert.event.blockageTotal]}`}
        />
      )}
      {!isCameraBlockageEvent && alert.event?.blockageRatio !== undefined && (
        <Tags.Label
          label={`Blockage ratio: ${alert.event.blockageRatio}`}
        />
      )}
      {alert.event?.blockageRegionLeft !== undefined && (
        <Tags.Label
          label={`Blockage region left: ${blockageStatus[alert.event.blockageRegionLeft]}`}
        />
      )}
      {alert.event?.blockageRegionCenter !== undefined && (
        <Tags.Label
          label={`Blockage region center: ${blockageStatus[alert.event.blockageRegionCenter]}`}
        />
      )}
      {alert.event?.blockageRegionRight !== undefined && (
        <Tags.Label
          label={`Blockage region right: ${blockageStatus[alert.event.blockageRegionRight]}`}
        />
      )}
      {alert.event?.cameraId !== undefined && (
        <Tags.Label
          label={`Camera ID: ${alert.event.cameraId}`}
        />
      )}
      {alert.event?.level !== undefined && (
        <Tags.Label
          label={`Level: ${alert.event.level}`}
        />
      )}
      {!shownTagSet.has('maxG')
        && maxGValue !== null
        && isAccelerationEvent && (
        <Tags.Label
          label={`Max G: ${formatG(maxGValue)}`}
        />
      )}
      {isAccelerationEvent && (
        <Tags.Label
          label={`Max G Detail: (${formatG(gX)}, ${formatG(gY)}, ${formatG(gZ)})`}
        />
      )}
      {isAccelerationEvent && (
        <Tags.Label
          label={`Accel Detail: (${formatAcceleration(alert.event?.maxXAcceleration)}, ${
            formatAcceleration(alert.event?.maxYAcceleration)
          }, ${formatAcceleration(alert.event?.maxZAcceleration)})`}
        />
      )}
      {!!alert.driverId && (alert.name === alertEventNames.DrivingOvertime) && showOvertimeLabel() && (
        <OvertimeTag
          level={alert.event.level || 0}
          driverId={alert.driverId}
          fleetId={alert.fleetId}
        />
      )}
      {!shownTagSet.has('trip') && !!tripNavigationTarget && (
        <Tags.Label
          label="Go to the trip"
          onClick={goToTrip}
        />
      )}
    </>
  );

  const hiddenDetailTags: HiddenDetailTagItem[] = [{
    key: 'all-alert-details',
    node: moreDetailsContent,
  }];

  return (
    <div className="tags-row">
      {!alert.isValid && (
        <Tags.Status
          label="Alert marked as invalid"
          backgroundColor={theme.colors.badge_invalid}
        />
      )}
      {alert.isValid && (
        <Tags.Status
          label="Valid alert"
          backgroundColor={theme.colors.badge_valid}
        />
      )}
      <Tags.Status
        label={capitalizeRiskLevel(alert.riskLevel)}
        backgroundColor={generateColorBasedOnRisk(alert.riskLevel, theme)}
      />
      {shownTagSet.has('time') && (
        <Tags.LabeledIcon
          label={`${getTime(alert.eventDate)} (${getTimeZone(alert.eventDate)})`}
          icon={IconKeys.TIME}
          flexible
        />
      )}
      {shownTagSet.has('date') && (
        <Tags.LabeledIcon
          label={getFormattedDateWithComma(alert.eventDate)}
          icon={IconKeys.DATE}
        />
      )}
      {/* {shownTagSet.has('vehicle') && (
        <Tags.LabeledIcon
          label={alert.vehicle?.licensePlateNumber}
          icon={generateVehicleTypeIcon(alert.vehicle?.category)}
          onClick={() => alert.event?.vehicleId && navigate(`/vehicles/${alert.event.vehicleId}`)}
        />
      )} */}
      {shownTagSet.has('alertType') && (
        <Tags.Toggler
          type={alertSensorMap[alert.sensor]}
          trigger={badge}
        />
      )}
      {shownTagSet.has('trip') && !!tripNavigationTarget && (
        <Tags.Label
          label="Go to the trip"
          onClick={goToTrip}
        />
      )}
      {shownTagSet.has('duration') && alert.event?.durationMs !== undefined && (
        <Tags.Label
          label={`Duration (ms): ${alert.event.durationMs}`}
        />
      )}
      {shownTagSet.has('configuredSpeedLimit')
        && (alert.event?.configSpeedLimit || alert.event?.detectedSpeedLimitMs !== undefined) && (
        <Tags.Label
          label={`Configured Speed Limit: ${
            alert.event?.configSpeedLimit ? alert.event?.configSpeedLimit : 0
          } km/h`}
        />
      )}
      {shownTagSet.has('maxG')
        && maxGValue !== null
        && isAccelerationEvent && (
        <Tags.Label
          label={`Max G: ${formatG(maxGValue)}`}
        />
      )}
      {alert.timeSynchronization === 'NOT_SYNCHRONIZED' && (
        <Tags.Status
          label="Alert time might be incorrect"
          backgroundColor={theme.colors.badge_invalid}
        />
      )}
      <HiddenDetails items={hiddenDetailTags} label="More details..." />
    </div>
  );
}

export default AlertTags;
